import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Cockpit Popover — the one true floating-menu shell.
 *
 * Renders into document.body with fixed positioning anchored to a trigger
 * element. This sidesteps the whole class of in-flow popover bugs:
 * stacking contexts from transformed/animated ancestors, z-index battles
 * between siblings, and clipping by overflow:hidden containers (tables,
 * cards, panels). Auto-flips above the anchor when there's no room below,
 * repositions on scroll/resize, closes on outside-mousedown and Escape.
 */
export interface PopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Horizontal alignment relative to the anchor. Default 'left'. */
  align?: 'left' | 'right';
  /** Match the anchor's width (for select-style menus). */
  matchWidth?: boolean;
  /** Gap between anchor and menu, px. Default 4. */
  offset?: number;
  minWidth?: number;
  /** Extra styles for the floating container. */
  style?: React.CSSProperties;
  role?: string;
  children?: React.ReactNode;
}

const VIEWPORT_PAD = 8;
// A popover always belongs to whatever is on top, so it has to clear the
// tallest thing that can host one. SidePanel stacks at 40 + layer * 20
// (overlay) and +1 (panel), so a layer-2 panel already reaches 81 — the old
// value of 80 put the menu *behind* the panel that opened it, silently, with
// no error. 150 clears panels up to layer 5 and still sits under Toast (200).
const POPOVER_Z = 150;

export default function Popover({
  open,
  anchorEl,
  onClose,
  align = 'left',
  matchWidth = false,
  offset = 4,
  minWidth,
  style,
  role,
  children,
}: PopoverProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({ top: 0, left: -9999 });

  const reposition = () => {
    const anchor = anchorEl;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    const rect = anchor.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    const next: React.CSSProperties = {};

    // Vertical: below by default, flip above when it overflows and there's room.
    const spaceBelow = window.innerHeight - rect.bottom - offset - VIEWPORT_PAD;
    const spaceAbove = rect.top - offset - VIEWPORT_PAD;
    const placeAbove = menuRect.height > spaceBelow && spaceAbove > spaceBelow;
    if (placeAbove) {
      next.top = Math.max(VIEWPORT_PAD, rect.top - offset - menuRect.height);
      next.maxHeight = Math.min(menuRect.height, spaceAbove);
    } else {
      next.top = rect.bottom + offset;
      next.maxHeight = Math.max(120, spaceBelow);
    }

    // Horizontal
    // minWidth can make a matched menu wider than its anchor; clamp with the width it will really have.
    const width = matchWidth ? Math.max(rect.width, minWidth ?? 0) : menuRect.width;
    let left = align === 'right' ? rect.right - width : rect.left;
    left = Math.min(Math.max(VIEWPORT_PAD, left), window.innerWidth - width - VIEWPORT_PAD);
    next.left = left;
    if (matchWidth) next.width = width;

    setPos(next);
  };

  // Position before paint so the entry animation plays from the right spot.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl]);

  // Track scroll/resize while open (capture catches inner scrollers).
  useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl]);

  // Outside-mousedown (anchor and menu both count as inside) + Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorEl && anchorEl.contains(t)) return;
      if (menuRef.current && menuRef.current.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorEl, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="ck-menu-pop"
      role={role}
      style={{
        position: 'fixed',
        zIndex: POPOVER_Z,
        overflowY: 'auto',
        padding: '4px',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-pop)',
        minWidth,
        ...pos,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
